import type { PartDefinition } from "@rogue/rackbuilder"
import { AdjustOnlyVisual } from "../../../constants"
import { getRackModel } from "../../../modelPaths"
import { WallmountBuilder } from "../wallmountBuilder"

export class RML390Full extends WallmountBuilder {
  // Override: Completely custom assembly for 390Full
  protected createSide(left: boolean): PartDefinition {
    const frontStick = this.createFrontStick(left)
    const backStick = this.createBackStick(left)
    const topCrossmember = this.createHorizontalCrossmember()
    const bottomCrossmember = this.createHorizontalCrossmember()
    const strapSafeties = this.createStrapSafeties(left)

    // Assemble front stick
    frontStick.slots['crossmember-top'] = topCrossmember
    frontStick.slots['crossmember-bottom'] = bottomCrossmember
    frontStick.slots['safety-high'] = strapSafeties

    // Attach back stick to bottom crossmember
    bottomCrossmember.slots.back = backStick
    
    // Add folding crossmembers to back stick
    backStick.slots['top-folding-crossmember'] = this.createTopCrossmember(left)
    backStick.slots['crossmember-bottom'] = this.createBottomCrossmember(left)
    
    // Add J-Cup to back stick
    backStick.slots['jcup-high'] = this.createJCup(left)

    return frontStick
  }

  // New method: Create front stick
  protected createFrontStick(left: boolean): PartDefinition {
    const stick: PartDefinition = {
      // All uprights are numbered except with the txt black color
      mesh: this.color !== 'powdercoat'
        ? getRackModel('WM_RML_PARTS', 'RML stick numbered')
        : getRackModel('WM_RML_PARTS', 'RML stick'),
      materials      : this.material,
      slots          : {},
      offset         : { x: 0.5 },
      adjustments    : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      adjustmentType : AdjustOnlyVisual,
    }

    // Add sticker to right side of front stick
    if (!left) {
      stick.slots['sticker-right'] = this.createBrandingSticker(false)
    }

    return stick
  }

  // New method: Create back stick
  protected createBackStick(left: boolean): PartDefinition {
    const stick: PartDefinition = {
      mesh: this.color !== 'powdercoat'
        ? getRackModel('WM_RML_PARTS', 'RML stick numbered')
        : getRackModel('WM_RML_PARTS', 'RML stick'),
      slots          : {},
      anchor         : 'crossmember-bottom',
      materials      : this.material,
      adjustments    : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      adjustmentType : AdjustOnlyVisual,
    }

    // Add sticker to left side of back stick
    if (left) {
      stick.slots['sticker-left'] = this.createBrandingSticker(true)
    }

    return stick
  }

  // New method: Create horizontal crossmembers (front to back)
  protected createHorizontalCrossmember(): PartDefinition {
    return {
      mesh      : getRackModel('RML_CROSSMEMBERS', 'crossmember 3'),
      slots     : {},
      anchor    : 'front',
      materials : { 
        main     : this.material.main, 
        hardware : { material: this.config.hardware }, 
      },
    }
  }

  // New method: Create strap safeties
  protected createStrapSafeties(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel('RML_SAFETY', 'strap safety 3'),
      slots       : {},
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
    }
  }

  // Override: Different crossmember mesh and offset
  protected createTopCrossmember(left: boolean): PartDefinition {
    return {
      mesh  : getRackModel('WM_RML_PARTS', 'RML folding back crossmember 9 no hardware'),
      slots : {
        back: {
          mesh   : getRackModel('WM_SHARED', 'hinge_assembly'),
          slots  : this.stringer ? { left: this.createStringer(left) } : {},
          anchor : '',
        },
      },
      materials : this.material,
      anchor    : 'top',
      offset    : { y: 8.4, x: -0.25 },
    }
  }

  // Override: Different crossmember offset
  protected createBottomCrossmember(left: boolean): PartDefinition {
    return {
      mesh  : getRackModel('WM_RML_PARTS', 'RML folding back crossmember 9 no hardware'),
      slots : {
        back: {
          mesh   : getRackModel('WM_SHARED', 'hinge_assembly'),
          slots  : this.stringer ? { left: this.createStringer(left) } : {},
          anchor : '',
        },
      },
      materials : this.material,
      anchor    : 'top',
      offset    : { x: -0.25 },
    }
  }

  // Override: Pullup attaches to top crossmember, not stick
  protected attachPullupBar(leftSide: PartDefinition): void {
    const pullupBar: PartDefinition = {
      mesh        : this.getPullupMesh(),
      slots       : {},
      anchor      : 'pin',
      adjustments : [
        { type: 'rotate', axis: 'X', amount: 90 }, 
        { type: 'flip', axis: 'Y' },
      ],
      offset: { x: -0.1 },
    }

    const topCrossmember = leftSide.slots['crossmember-top'] as PartDefinition
    topCrossmember.slots['pin-front'] = pullupBar
  }
}